<template>
  <div class="grn-wrapper">
    <!-- Toolbar (no-print) -->
    <div class="grn-toolbar no-print d-flex align-center px-6 py-3">
      <v-icon
        class="mr-2"
        color="secondary">
        mdi-file-document-check-outline
      </v-icon>
      <div>
        <div class="caption grey--text text-uppercase letter-spacing-1">
          เอกสารอ้างอิงการรับสินค้า
        </div>
        <div class="subtitle-1 font-weight-bold secondary--text">
          <template v-if="$route.params.id">
            {{ bookings.length ? bookings[0].bookingNo : '—' }}
          </template>
          <template v-else-if="$route.query.date">
            ประจำวันที่ {{ $dayjs($route.query.date, 'DD-MM-YYYY').locale('th').format('DD MMMM YYYY') }}
          </template>
        </div>
      </div>
      <v-spacer />
      <v-btn
        depressed
        color="secondary"
        class="mr-2"
        @click="printPage">
        <v-icon
          left
          small>
          mdi-printer
        </v-icon>
        พิมพ์เอกสาร
      </v-btn>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="no-print d-flex justify-center align-center pa-12">
      <v-progress-circular
        indeterminate
        color="secondary" />
    </div>

    <!-- Error state -->
    <div
      v-if="!loading && loadError"
      class="no-print d-flex flex-column align-center justify-center pa-16">
      <v-icon
        size="72"
        color="error lighten-1">
        mdi-alert-circle-outline
      </v-icon>
      <div class="title font-weight-bold error--text mt-4">
        โหลดข้อมูลไม่สำเร็จ
      </div>
      <div class="body-2 grey--text mt-2 text-center">
        ไม่สามารถดึงข้อมูลจากระบบได้<br>กรุณาตรวจสอบการเชื่อมต่อและลองใหม่อีกครั้ง
      </div>
      <v-btn
        depressed
        color="error"
        class="mt-6 white--text"
        small
        @click="$router.back()">
        <v-icon
          left
          small>
          mdi-arrow-left
        </v-icon>
        กลับหน้าก่อนหน้า
      </v-btn>
    </div>

    <!-- Empty state -->
    <div
      v-if="!loading && !loadError && bookings.length === 0"
      class="no-print d-flex flex-column align-center justify-center pa-16">
      <v-icon
        size="64"
        color="grey lighten-2">
        mdi-file-document-remove-outline
      </v-icon>
      <div class="subtitle-1 font-weight-bold grey--text mt-4">
        ไม่มีรายการจองในวันที่เลือก
      </div>
      <div class="body-2 grey--text mt-1">
        ไม่พบรายการที่มีสถานะรอรับสินค้า
      </div>
      <v-btn
        depressed
        outlined
        color="secondary"
        class="mt-6"
        small
        :to="{ name: 'DeliveryBookingList' }">
        <v-icon
          left
          small>
          mdi-arrow-left
        </v-icon>
        กลับไปหน้ารายการจอง
      </v-btn>
    </div>

    <!-- Documents -->
    <template v-if="!loading && !loadError && bookings.length > 0">
      <div
        v-for="(booking, bIdx) in bookings"
        :key="booking.id"
        :class="['grn-page', { 'grn-page-break': bIdx < bookings.length - 1 }]">
        <!-- Repeating band (print only) — thead repeats on every physical page -->
        <table class="grn-repeat-table">
          <thead>
            <tr class="py-4">
              <td>
                <div class="grn-repeat-band">
                  {{ booking.bookingNo }}
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="grn-repeat-body">
                <!-- Doc Header -->
                <div class="grn-doc-header">
                  <div class="grn-doc-title-block">
                    <div class="d-flex align-center mb-1">
                      <v-icon
                        color="secondary"
                        class="mr-2">
                        mdi-clipboard-check-outline
                      </v-icon>
                      <div>
                        <div class="grn-title">
                          ใบรับสินค้า
                        </div>
                        <div class="grn-subtitle">
                          Goods Receiving Note (GRN)
                        </div>
                      </div>
                    </div>
                    <div class="mt-2">
                      <v-chip
                        small
                        label
                        :color="statusColor(booking.status)"
                        :text-color="statusTextColor(booking.status)"
                        class="font-weight-medium">
                        สถานะ: {{ statusLabel(booking.status) }}
                      </v-chip>
                    </div>
                  </div>
                  <div class="grn-doc-ref-block">
                    <div class="grn-ref-label">
                      เลขที่อ้างอิง / REFERENCE NO.
                    </div>
                    <div class="grn-ref-no">
                      {{ booking.bookingNo }}
                    </div>
                    <div class="grn-ref-date">
                      วันที่ออกเอกสาร: {{ issuedDate }}
                    </div>
                  </div>
                </div>

                <div class="grn-divider" />

                <!-- Info Section -->
                <div class="grn-info-grid">
                  <!-- Left: vendor + vehicle -->
                  <div class="grn-info-col">
                    <div class="grn-section-label">
                      <v-icon
                        small
                        color="secondary"
                        class="mr-1">
                        mdi-domain
                      </v-icon>
                      ข้อมูลโรงงาน
                    </div>
                    <table class="grn-info-table">
                      <tr>
                        <td class="grn-info-key">
                          ชื่อโรงงาน:
                        </td>
                        <td class="grn-info-val">
                          {{ primaryVendorName(booking) }}
                        </td>
                      </tr>
                      <tr v-if="booking.vehicles && booking.vehicles.length">
                        <td class="grn-info-key">
                          ประเภทยานพาหนะ:
                        </td>
                        <td class="grn-info-val">
                          <span
                            v-for="(v, i) in booking.vehicles"
                            :key="i">
                            {{ v.type.th }} ×{{ v.count }}<br v-if="i < booking.vehicles.length - 1">
                          </span>
                        </td>
                      </tr>
                      <tr v-if="booking.packages && booking.packages.length">
                        <td class="grn-info-key">
                          ประเภทบรรจุภัณฑ์:
                        </td>
                        <td class="grn-info-val">
                          <span
                            v-for="(p, i) in booking.packages"
                            :key="i">
                            {{ p.type.th }} ×{{ p.count }}<br v-if="i < booking.packages.length - 1">
                          </span>
                        </td>
                      </tr>
                      <tr v-if="booking.createdBy">
                        <td class="grn-info-key">
                          จองโดย:
                        </td>
                        <td class="grn-info-val">
                          {{ booking.createdBy.name }}
                          <span v-if="booking.createdBy.tel"><br>{{ booking.createdBy.tel }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.note">
                        <td class="grn-info-key">
                          หมายเหตุ:
                        </td>
                        <td class="grn-info-val">
                          {{ booking.note }}
                        </td>
                      </tr>
                    </table>
                  </div>

                  <!-- Right: booking detail -->
                  <div class="grn-info-col">
                    <div class="grn-section-label">
                      <v-icon
                        small
                        color="secondary"
                        class="mr-1">
                        mdi-calendar-clock
                      </v-icon>
                      รายละเอียดการจองและคำสั่งซื้อ
                    </div>
                    <table class="grn-info-table">
                      <tr>
                        <td class="grn-info-key">
                          วันที่นัดหมาย:
                        </td>
                        <td class="grn-info-val font-weight-medium">
                          {{ appointmentDate(booking) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="grn-info-key">
                          ช่วงเวลา (Slot):
                        </td>
                        <td class="grn-info-val font-weight-medium">
                          {{ slotTime(booking) }}
                        </td>
                      </tr>
                      <tr v-if="poList(booking).length > 0">
                        <td class="grn-info-key">
                          เลขที่ใบสั่งซื้อ (PO):
                        </td>
                        <td class="grn-info-val">
                          <div
                            v-for="po in poList(booking)"
                            :key="po.poOrderNo"
                            class="secondary--text font-weight-bold">
                            {{ po.poOrderNo || '-' }}
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>

                <div class="grn-divider" />

                <!-- PO → SKU List -->
                <div
                  v-for="(po, poIdx) in poList(booking)"
                  :key="po.poOrderNo || poIdx"
                  class="grn-po-block">
                  <div class="grn-po-header">
                    <div class="d-flex align-center grn-po-heading">
                      <v-icon
                        small
                        color="secondary"
                        class="mr-2">
                        mdi-file-document-outline
                      </v-icon>
                      <span
                        v-if="po.poOrderNo"
                        class="grn-po-title">
                        <strong class="secondary--text">{{ po.poOrderNo }}</strong>
                      </span>
                      <span
                        v-if="po.locationCode"
                        class="grn-po-location ml-2">
                        <v-icon
                          x-small
                          color="success darken-1"
                          class="mr-1">
                          mdi-transfer-right
                        </v-icon>
                        รับเข้า
                        <v-chip
                          x-small
                          color="green darken-3"
                          text-color="white"
                          class="ml-1 font-weight-bold">
                          {{ po.locationCode }}
                        </v-chip>
                      </span>
                      <span
                        v-if="po.vendorName"
                        class="grn-po-vendor ml-2 grey--text">
                        | {{ po.vendorName }}
                      </span>
                    </div>
                    <v-spacer />
                    <v-chip
                      x-small
                      :color="po.isManual ? 'orange lighten-4' : 'blue lighten-4'"
                      class="ml-2">
                      {{ po.isManual ? 'PO นอกระบบ' : 'PO ในระบบ' }}
                    </v-chip>
                  </div>

                  <table class="grn-sku-table">
                    <thead v-if="!po.isManual">
                      <tr>
                        <th style="width:20%">
                          รหัสสินค้า (SKU Code)
                        </th>
                        <th style="width:32%">
                          รายละเอียดสินค้า
                        </th>
                        <th style="width:14%">
                          สี / ไซส์
                        </th>
                        <th style="width:24%">
                          Collection
                        </th>
                        <th
                          style="width:12%"
                          class="text-right">
                          จำนวนที่จัดส่ง
                        </th>
                      </tr>
                    </thead>
                    <thead v-else>
                      <tr>
                        <th style="width:88%">
                          ชื่อสินค้า
                        </th>
                        <th
                          style="width:12%"
                          class="text-right">
                          จำนวนที่จัดส่ง
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(line, li) in po.lines || []"
                        :key="li">
                        <template v-if="po.isManual">
                          <td>{{ line.name || '-' }}</td>
                          <td class="text-right font-weight-bold secondary--text">
                            {{ line.deliveryQty != null ? line.deliveryQty.toLocaleString() : '-' }}
                          </td>
                        </template>
                        <template v-else>
                          <td class="grn-sku-code">
                            {{ line.itemNo }}
                          </td>
                          <td>{{ line.description || '-' }}</td>
                          <td>
                            <span v-if="line.color || line.size">
                              {{ [line.color, line.size].filter(Boolean).map((s) => s.toUpperCase()).join(' / ') }}
                            </span>
                            <span
                              v-else
                              class="grey--text">-</span>
                          </td>
                          <td>
                            <span v-if="line.gwCollection">{{ line.gwCollection }}</span>
                            <span
                              v-else
                              class="grey--text">-</span>
                          </td>
                          <td class="text-right font-weight-bold secondary--text">
                            {{ line.deliveryQty != null ? line.deliveryQty.toLocaleString() : '-' }}
                          </td>
                        </template>
                      </tr>
                      <tr
                        v-if="!po.lines || po.lines.length === 0"
                        class="grn-empty-row">
                        <td
                          :colspan="po.isManual ? 2 : 5"
                          class="text-center grey--text">
                          ไม่มีรายการสินค้า
                        </td>
                      </tr>
                      <tr class="grn-total-row">
                        <td
                          :colspan="po.isManual ? 1 : 4"
                          class="text-right font-weight-bold">
                          ยอดรวมทั้งหมด (TOTAL)
                        </td>
                        <td class="text-right font-weight-bold secondary--text">
                          {{ poTotal(po).toLocaleString() }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Grand total -->
                <div
                  v-if="poList(booking).length > 1"
                  class="grn-grand-total">
                  <span class="mr-3">ยอดรวมทุกรายการ (GRAND TOTAL)</span>
                  <span class="secondary--text font-weight-bold">{{ grandTotal(booking).toLocaleString() }} รายการ</span>
                </div>

                <!-- QR + Signature (keep together on print) -->
                <div class="grn-bottom-block">
                  <!-- QR Section -->
                  <div class="grn-qr-section">
                    <div class="grn-qr-box">
                      <img
                        v-if="qrMap[booking.id]"
                        :src="qrMap[booking.id]"
                        class="grn-qr-img"
                        alt="QR Code">
                      <div
                        v-else
                        class="grn-qr-placeholder">
                        <v-icon
                          size="64"
                          color="grey lighten-1">
                          mdi-qrcode
                        </v-icon>
                      </div>
                    </div>
                    <div class="grn-qr-label">
                      สแกนเพื่อยืนยันการส่งสินค้า
                    </div>
                    <div class="grn-qr-sublabel">
                      (Scan to Confirm Delivery)
                    </div>
                    <div class="grn-qr-hash">
                      BOOKING REF: {{ booking.bookingNo }}
                    </div>
                  </div>

                  <div class="grn-divider" />

                  <!-- Signature Section -->
                  <div class="grn-signature-section">
                    <div class="grn-sig-col">
                      <div class="grn-sig-title">
                        ผู้นำส่งสินค้า (DRIVER SIGNATURE)
                      </div>
                      <div class="grn-sig-line" />
                      <div class="grn-sig-footer">
                        <div class="grn-sig-row">
                          <span class="grn-sig-label">ลงชื่อ</span>
                          <span class="grn-sig-dot-line" />
                        </div>
                        <div class="grn-sig-row">
                          <span class="grn-sig-label">วันที่</span>
                          <span class="grn-sig-dot-line" />
                        </div>
                      </div>
                    </div>
                    <div class="grn-sig-col">
                      <div class="grn-sig-title">
                        เจ้าหน้าที่คลังสินค้า (AUTHORIZED RECEIVER)
                      </div>
                      <div class="grn-sig-line" />
                      <div class="grn-sig-footer">
                        <div class="grn-sig-row">
                          <span class="grn-sig-label">ลงชื่อ</span>
                          <span class="grn-sig-dot-line" />
                        </div>
                        <div class="grn-sig-row">
                          <span class="grn-sig-label">วันที่</span>
                          <span class="grn-sig-dot-line" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div><!-- end grn-bottom-block -->
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import DeliveryBookingProvider from '@/resources/DeliveryBookingProvider'
import { mapActions } from 'vuex'
import { sortByTimeFn } from '@/assets/js/DateTimeUtils'
import { generateQRCode } from '@/assets/js/helper'
import {
  statusLabel,
  statusBg,
  statusTextColor
} from '@/assets/js/BookingStatusUtils'

const deliveryBookingService = new DeliveryBookingProvider()

export default {
  name: 'DeliveryBookingGRN',

  data () {
    return {
      bookings: [],
      loading: false,
      loadError: false,
      qrMap: {}
    }
  },

  computed: {
    issuedDate () {
      return this.$dayjs().locale('th').format('DD MMMM YYYY')
    },

    generatedAt () {
      return this.$dayjs().format('DD/MM/YYYY HH:mm:ss')
    }
  },

  async mounted () {
    if (this.$route.params.id) {
      await this.fetchSingle()
    } else if (this.$route.query.date) {
      await this.fetchByDate()
    }
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),

    // ─── Fetch ───────────────────────────────────────────────

    async fetchSingle () {
      this.loading = true
      try {
        const { data } = await deliveryBookingService.getWarehouseBookingById(this.$route.params.id)
        this.bookings = [data]
        await this.generateQR(data)
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    async fetchByDate () {
      this.loading = true
      try {
        const apiDate = this.$dayjs(this.$route.query.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        const { data } = await deliveryBookingService.getWarehouseBookings({
          startDate: apiDate,
          endDate: apiDate,
          status: 'pending,noshow',
          limit: 999
        })
        const bookings = data.results || []
        this.bookings = bookings
        await Promise.all(bookings.map((b) => this.generateQR(b)))
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    async generateQR (booking) {
      const qr = await generateQRCode(`${process.env.VUE_APP_DELIVERY_BOOKING_URL}/delivery/${booking.bookingUuid || ''}`)
      this.$set(this.qrMap, booking.id, qr)
    },

    // ─── Per-booking helpers ──────────────────────────────────

    poList (booking) {
      return booking?.purchaseOrders || []
    },

    primaryVendorName (booking) {
      const names = this.poList(booking).map((po) => po.vendorName).filter(Boolean)
      return [...new Set(names)].join(', ') || '-'
    },

    sortedSlots (booking) {
      const slots = booking?.slots || []
      return slots.length ? sortByTimeFn(slots, (s) => s.startTime) : []
    },

    appointmentDate (booking) {
      if (!booking?.date) return '-'
      return this.$dayjs(booking.date).locale('th').format('DD MMMM YYYY')
    },

    slotTime (booking) {
      const slots = this.sortedSlots(booking)
      if (!slots.length) return '-'
      return `${slots[0].startTime} - ${slots[slots.length - 1].endTime} น.`
    },

    grandTotal (booking) {
      return this.poList(booking).reduce((sum, po) => sum + this.poTotal(po), 0)
    },

    poTotal (po) {
      return (po.lines || []).reduce((sum, l) => sum + (l.deliveryQty || 0), 0)
    },

    // ─── Status ───────────────────────────────────────────────

    statusLabel: (s) => statusLabel(s),
    statusColor: (s) => statusBg(s),
    statusTextColor: (s) => statusTextColor(s),

    // ─── Print ───────────────────────────────────────────────

    printPage () {
      window.print()
    }
  }
}
</script>

<style scoped>
/* ── Wrapper ── */
.grn-wrapper {
  /* background: #f0f2f5; */
  min-height: 100vh;
}

/* ── Toolbar ── */
.grn-toolbar {
  background: #f0f2f5;
  border-bottom: 1px solid #dde1e7;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* ── Page ── */
.grn-page {
  max-width: 860px;
  margin: 32px auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.08);
  padding: 40px 48px;
  font-size: 13px;
  color: #1a1a1a;
}

/* ── Doc Header ── */
.grn-doc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.grn-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a237e;
  line-height: 1.2;
}

.grn-subtitle {
  font-size: 12px;
  color: #888;
}

.grn-doc-ref-block {
  text-align: right;
}

.grn-ref-label {
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.grn-ref-no {
  font-size: 20px;
  font-weight: 800;
  color: #1a237e;
  letter-spacing: 1px;
}

.grn-ref-date {
  font-size: 11px;
  color: #666;
  margin-top: 2px;
}

/* ── Divider ── */
.grn-divider {
  border-top: 1px solid #e0e0e0;
  margin: 20px 0;
}

/* ── Info Grid ── */
.grn-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 4px;
}

.grn-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #1a237e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.grn-info-table {
  width: 100%;
  border-collapse: collapse;
}

.grn-info-table tr td {
  padding: 3px 0;
  vertical-align: top;
}

.grn-info-key {
  font-size: 12px;
  color: #777;
  white-space: nowrap;
  padding-right: 10px !important;
  width: 140px;
}

.grn-info-val {
  font-size: 12px;
  color: #1a1a1a;
}

/* ── PO Block ── */
.grn-po-block {
  margin-bottom: 24px;
}

.grn-po-header {
  display: flex;
  align-items: center;
  background: #f8f9fd;
  /* border-left: 3px solid #3949ab; */
  padding: 8px 12px;
  /* border-radius: 0 4px 4px 0; */
  margin-bottom: 0;
  page-break-after: avoid;
  page-break-inside: avoid;
}

.grn-po-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a237e;
}

.grn-po-no {
  font-size: 12px;
  color: #444;
}

.grn-po-vendor {
  font-size: 11px;
}

/* ── SKU Table ── */
.grn-sku-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.grn-sku-table thead tr {
  background: #f5f5f5;
}

.grn-sku-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  color: #666;
  font-weight: 600;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.grn-sku-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.grn-sku-table tbody tr:last-child td {
  border-bottom: none;
}

.grn-sku-code {
  font-family: monospace;
  font-size: 12px;
  white-space: nowrap;
}

.grn-empty-row td {
  padding: 16px !important;
  font-size: 12px;
}

.grn-sku-table tbody .grn-total-row td {
  padding: 8px 10px;
  border-top: 2px solid #e0e0e0;
  background: #fafafa;
  font-size: 13px;
}

/* ── Grand Total ── */
.grn-grand-total {
  text-align: right;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
}

/* ── QR Section ── */
.grn-qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.grn-qr-box {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  margin-bottom: 10px;
}

.grn-qr-img {
  width: 120px;
  height: 120px;
  display: block;
}

.grn-qr-placeholder {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.grn-qr-label {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2px;
}

.grn-qr-sublabel {
  font-size: 12px;
  color: #888;
  margin-bottom: 6px;
}

.grn-qr-hash {
  font-size: 10px;
  color: #aaa;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* ── Signature ── */
.grn-signature-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  padding: 8px 0 16px;
}

.grn-sig-title {
  font-size: 12px;
  font-weight: 600;
  color: #444;
  margin-bottom: 48px;
}

.grn-sig-line {
  border-bottom: 1px solid #bbb;
  margin-bottom: 8px;
}

.grn-sig-footer {
  font-size: 11px;
  color: #666;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.grn-sig-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.grn-sig-label {
  white-space: nowrap;
  width: 40px;
  flex-shrink: 0;
  padding-bottom: 2px;
}

.grn-sig-dot-line {
  flex: 1;
  border-bottom: 1px dotted #999;
  margin-bottom: 2px;
}

/* ── Doc Footer ── */
.grn-doc-footer {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #aaa;
  padding-top: 8px;
}

/* ── Print ── */
/* ── Repeating header table ── */
.grn-repeat-table {
  width: 100%;
  border-collapse: collapse;
}

.grn-repeat-body {
  padding: 0;
}

/* band ซ่อนบน screen แสดงเฉพาะ print */
.grn-repeat-band {
  display: none;
}

@media print {
  .grn-repeat-band {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 6px 0 10px;
    font-size: 10px;
    font-family: monospace;
    color: #aaa;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 20px;
  }
}

.grn-page-break {
  page-break-after: always;
}

.grn-bottom-block {
  page-break-inside: avoid;
}

/* ── Footer ref ── */
.grn-doc-footer-ref {
  font-family: monospace;
  letter-spacing: 0.5px;
}

@media print {
  .no-print {
    display: none !important;
  }

  .grn-wrapper {
    background: #fff;
  }

  .grn-page {
    margin: 0;
    box-shadow: none;
    border-radius: 0;
    padding: 0;
    max-width: 100%;
  }

}
</style>

<style>
@page {
  margin: 16mm 22mm;
  @bottom-right {
    content: "หน้า " counter(page) " / " counter(pages);
    font-size: 10px;
    color: #aaa;
    font-family: monospace;
  }
}

/* keep v-chip background colors when printing (browsers strip them by default) */
@media print {
  .grn-po-location .v-chip {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
